import { CircleMarker, Tooltip } from 'react-leaflet';
import { zetDriehoekAan } from './driehoek';
import { STATUS_KLEUREN, type KaartStatus, type Registratie, type Woning } from '@/types/domain';
import { adresRegel } from '@/utils/format';
import { tooltipRegels } from './tooltiptekst';

/** Eén punt op de kaart: een BAG-woning of een handmatig geplaatst punt. */
export interface KaartPunt {
  /** Uniek binnen de kaart; BAG-id, of het registratie-id bij handmatige punten. */
  sleutel: string;
  woning: Woning;
  status: KaartStatus;
  /** Handmatig geplaatst; wordt met een dikkere rand getekend. */
  handmatig: boolean;
  /** Huurwoning van de corporatie; krijgt een ring om de stip. */
  huur: boolean;
  /** De registratie bij deze woning, als die er is; alleen voor de tooltip. */
  registratie?: Registratie;
}

interface WoningLaagProps {
  punten: KaartPunt[];
  geselecteerdeSleutel: string | null;
  zichtbareStatussen: Set<KaartStatus>;
  /** Huur en koop apart aan of uit te zetten. */
  soort: { huur: boolean; koop: boolean };
  zoom: number;
  bijKlik: (punt: KaartPunt) => void;
}

zetDriehoekAan();

/** Straal in pixels; op lage zoomniveaus kleiner zodat de kaart leesbaar blijft. */
function straalVoorZoom(zoom: number, geselecteerd: boolean): number {
  const basis = zoom >= 19 ? 11 : zoom >= 18 ? 9 : zoom >= 17 ? 7 : zoom >= 16 ? 5.5 : 4;
  return geselecteerd ? basis + 4 : basis;
}

/**
 * De rand rond een stip.
 *
 * Wit maakt elke kleur los van de luchtfoto — behalve de witte stip van "nog
 * niet bezocht", die daarin zou oplossen. Die krijgt de donkerblauwe rand.
 */
function randkleur(status: KaartStatus, geselecteerd: boolean, handmatig: boolean): string {
  if (status === 'Nog niet bezocht') return '#1c2445';
  if (geselecteerd) return '#ffffff';
  if (handmatig) return '#1c2445';
  return 'rgba(255,255,255,0.85)';
}

/**
 * Alle punten als klikbare stippen: een cirkel bij koop, een driehoek bij
 * huur.
 *
 * Bewust Leaflets eigen CircleMarker en geen markers met een eigen tekening:
 * die worden op het canvas geplaatst en blijven bij elke zoombeweging exact
 * op hun plek staan. Een eigen tekening moet Leaflet als los element
 * bijhouden, en dan verspringen de punten zichtbaar zodra een zoomanimatie
 * klaar is.
 *
 * De kleur wordt afgeleid uit de registratie; woningen zonder registratie zijn
 * wit. Handmatig geplaatste punten krijgen een donkere rand, zodat ze te
 * onderscheiden zijn van BAG-woningen.
 */
export function WoningLaag({
  punten,
  geselecteerdeSleutel,
  zichtbareStatussen,
  soort,
  zoom,
  bijKlik,
}: WoningLaagProps): JSX.Element {
  const zichtbaar = punten.filter(
    (punt) => zichtbareStatussen.has(punt.status) && (punt.huur ? soort.huur : soort.koop)
  );

  return (
    <>
      {zichtbaar
        .map((punt) => {
          const geselecteerd = punt.sleutel === geselecteerdeSleutel;
          return (
            <CircleMarker
              key={punt.sleutel}
              center={[punt.woning.latitude, punt.woning.longitude]}
              radius={straalVoorZoom(zoom, geselecteerd)}
              pathOptions={{
                color: randkleur(punt.status, geselecteerd, punt.handmatig),
                weight: geselecteerd ? 3 : punt.handmatig ? 2.5 : 1.5,
                fillColor: STATUS_KLEUREN[punt.status],
                fillOpacity: 0.95,
                // Huurwoningen als driehoek. De vorm zegt dat de corporatie
                // het pand afkoppelt; de kleur blijft vrij om te vertellen
                // hoe ver het met de bewoner staat.
                driehoek: punt.huur,
              }}
              eventHandlers={{ click: () => bijKlik(punt) }}
            >
              {/*
                Elke regel noemt zijn eigen onderwerp. Dat is geen opmaak maar
                de kern: "koppelt niet af" naast "corporatie koppelt af" leest
                als een tegenspraak zolang er niet bij staat over wie het gaat.
              */}
              <Tooltip direction="top" offset={[0, -6]} opacity={1} className="kaarttip">
                <div className="kaarttip__kop">
                  <strong>{adresRegel(punt.woning.adres) || 'Handmatig punt'}</strong>
                  <span className="kaarttip__soort">
                    {punt.huur ? 'huurwoning' : 'koopwoning'}
                  </span>
                </div>
                <dl className="kaarttip__regels">
                  {tooltipRegels(punt.registratie, punt.huur).map((regel) => (
                    <div key={regel.wie} className="kaarttip__regel">
                      <dt>{regel.wie}</dt>
                      <dd>{regel.wat}</dd>
                    </div>
                  ))}
                </dl>
                {punt.handmatig && <p className="kaarttip__voet">handmatig geplaatst punt</p>}
              </Tooltip>
            </CircleMarker>
          );
        })}
    </>
  );
}
